// VOID: Card Warfare - Ranked Ladder / Global Leaderboard Modal (Fullscreen)
import { getAuth, getDisplayName, getMMR } from '../net/playerIdentity.js';
import { showCallsignModal } from './callsignModal.js';
import { checkAlphaAccess } from './alphaGate.js';
import { getIconSvg } from './icons.js';

let activeOverlay = null;

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function getRankTier(mmr) {
  if (mmr >= 1800) return { name: 'Grandmaster', icon: getIconSvg('rank_grandmaster', 14), color: '#ff0055' };
  if (mmr >= 1700) return { name: 'Master', icon: getIconSvg('rank_master', 14), color: '#a855f7' };
  if (mmr >= 1600) return { name: 'Diamond', icon: getIconSvg('rank_diamond', 14), color: '#00f0ff' };
  if (mmr >= 1500) return { name: 'Platinum', icon: getIconSvg('rank_platinum', 14), color: '#3b82f6' };
  if (mmr >= 1400) return { name: 'Gold', icon: getIconSvg('rank_gold', 14), color: '#eab308' };
  return { name: 'Cadet', icon: getIconSvg('rank_cadet', 14), color: '#94a3b8' };
}

export async function showLeaderboardModal(game) {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
  }
  game?.overlayGate?.acquire?.();

  const audio = game?.audio;
  const auth = getAuth();
  const hasAccount = !!(auth && auth.accountId);
  const myName = getDisplayName() || 'Commander';
  const myMmr = getMMR();
  const myTier = getRankTier(myMmr);

  const layer = el('div', 'leaderboard-overlay fullscreen-dialog-overlay');

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-bg-image"></div>
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>

    <div class="fullscreen-dialog-container">
      <header class="fullscreen-dialog-header">
        <div class="fullscreen-dialog-header-left">
          <div class="fullscreen-dialog-category">ARENA</div>
          <h2 class="fullscreen-dialog-title">LEADERBOARD</h2>
        </div>
        <div class="fullscreen-dialog-header-actions">
          <div class="fullscreen-dialog-status-pill status-pill-circular status-gold" title="Season 01 Live Telemetry">
            <span class="status-indicator-dot" style="background: #fbbf24; box-shadow: 0 0 8px #fbbf24;"></span>
          </div>
          <button type="button" class="modal-close-icon-btn" id="leaderboard-header-close-btn" aria-label="Close Leaderboard" title="Close (Esc)">
            <svg class="dialog-close-svg" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="2.5" y1="2.5" x2="11.5" y2="11.5" />
              <line x1="11.5" y1="2.5" x2="2.5" y2="11.5" />
            </svg>
          </button>
        </div>
      </header>

      <div class="fullscreen-dialog-body">
        <div class="leaderboard-switch-bar">
          <div class="leaderboard-mode-switch" id="leaderboard-mode-switch">
            <button type="button" class="leaderboard-tab-btn active" id="btn-lb-1v1" data-mode="1v1">1v1 DUEL</button>
            <button type="button" class="leaderboard-tab-btn" id="btn-lb-2v2" data-mode="2v2">2v2 DUO</button>
          </div>
        </div>

        <div class="leaderboard-fullscreen-layout">
          <!-- Left Panel: My Ranked Profile (Clean, flat, generous spacing) -->
          <div class="fullscreen-card-panel leaderboard-hero-panel" style="display: flex; flex-direction: column; justify-content: flex-start; gap: clamp(28px, 5vh, 42px);">
            <!-- Profile summary -->
            <div style="display: flex; align-items: center; justify-content: space-between; padding-bottom: 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.08);">
              <div>
                <div id="leaderboard-dossier-name" style="font-family: 'Orbitron', sans-serif; font-size: 17px; font-weight: 700; color: #f8fafc; letter-spacing: 1px;">
                  ${escapeHtml(myName)}
                </div>
                <div style="font-family: 'IBM Plex Mono', monospace; font-size: 11.5px; color: #94a3b8; margin-top: 3px;">
                  ${hasAccount ? `ID: #${auth.accountId}` : 'STATUS: GUEST // UNLINKED'}
                </div>
              </div>
              <div id="leaderboard-rank-badge" style="text-align: right;">
                <span style="font-family: 'Orbitron', sans-serif; font-size: 26px; font-weight: 800; color: #00f0ff;">--</span>
                <div style="font-family: 'Rajdhani', sans-serif; font-size: 10px; font-weight: 700; color: #64748b; letter-spacing: 1px;">GLOBAL RANK</div>
              </div>
            </div>

            <!-- Rating & Tier KPI -->
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                <span class="stat-label">ARENA RATING</span>
                <span class="stat-label" style="color: ${myTier.color}; font-weight: 700;">${myTier.icon} ${myTier.name}</span>
              </div>
              <div style="display: flex; align-items: baseline; gap: 6px; margin: 4px 0;">
                <span class="kpi-main-stat" id="leaderboard-my-mmr" style="color: #00f0ff; font-size: 26px;">${myMmr}</span>
                <span class="stat-label">MMR</span>
              </div>
              <div class="progress-mini-bar" style="background: rgba(255, 255, 255, 0.08); height: 6px;">
                <div class="progress-mini-fill" id="leaderboard-tier-progress" style="width: ${Math.min(100, Math.max(10, ((myMmr - 1000) / 1000) * 100))}%; background: ${myTier.color};"></div>
              </div>
              <div class="kpi-sub-stat" id="leaderboard-tier-next-hint" style="margin-top: 6px;">Season Peak: ${myMmr} MMR</div>
            </div>

            <!-- Combat Records KPI -->
            <div id="leaderboard-combat-box">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                <span class="stat-label">RECORD</span>
                <span class="stat-label" id="leaderboard-winrate-label">--% WIN</span>
              </div>
              <div style="display: flex; align-items: baseline; gap: 8px; margin: 4px 0;">
                <span class="kpi-main-stat" id="leaderboard-record-stat" style="font-size: 20px;">--</span>
              </div>
              <div class="progress-mini-bar" style="background: rgba(255, 255, 255, 0.08); height: 6px;">
                <div class="progress-mini-fill kpi-mini-fill-emerald" id="leaderboard-winrate-fill" style="width: 50%;"></div>
              </div>
              <div class="kpi-sub-stat" id="leaderboard-record-sub" style="margin-top: 6px;">Awaiting ranked arena deployment</div>
            </div>
          </div>

          <!-- Right Panel: Global Top 50 Table -->
          <div class="fullscreen-card-panel leaderboard-table-panel">
            <div class="leaderboard-table-container" id="leaderboard-table-wrapper" style="flex: 1; overflow-y: auto; padding: 12px 16px;">
              <div style="padding: 30px; text-align: center; color: #64748b; font-family: 'Rajdhani', sans-serif; font-size: 13px;">
                Connecting to Central Command Telemetry...
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Centered Bottom Action Button -->
      <footer class="fullscreen-dialog-footer">
        <button type="button" class="fullscreen-return-btn" id="leaderboard-return-btn">
          <span class="start-btn-text">RETURN</span>
        </button>
      </footer>
    </div>
  `;

  document.body.appendChild(layer);
  activeOverlay = layer;

  // Close handlers
  const closeModal = () => {
    audio?.playUiClick?.();
    layer.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
    window.removeEventListener('keydown', handleKey);
  };

  const returnBtn = layer.querySelector('#leaderboard-return-btn');
  returnBtn?.addEventListener('click', closeModal);
  const headerCloseBtn = layer.querySelector('#leaderboard-header-close-btn');
  headerCloseBtn?.addEventListener('click', closeModal);

  const handleKey = (e) => {
    if (e.key === 'Escape') closeModal();
  };
  window.addEventListener('keydown', handleKey);

  // Callsign edit button
  const editNameBtn = layer.querySelector('#leaderboard-edit-name-btn');
  editNameBtn?.addEventListener('click', async () => {
    audio?.playUiClick?.();
    const granted = await checkAlphaAccess({ prompt: true });
    if (!granted) return;

    showCallsignModal({
      game,
      onSaved: (newName) => {
        const updated = newName || getDisplayName() || 'Commander';
        const nameEl = layer.querySelector('#leaderboard-dossier-name');
        if (nameEl) nameEl.textContent = updated;
      }
    });
  });

  // Fetch telemetry in parallel with mode toggle
  const tableWrapper = layer.querySelector('#leaderboard-table-wrapper');
  const btn1v1 = layer.querySelector('#btn-lb-1v1');
  const btn2v2 = layer.querySelector('#btn-lb-2v2');

  const loadModeData = async (mode) => {
    btn1v1?.classList.toggle('active', mode === '1v1');
    btn2v2?.classList.toggle('active', mode === '2v2');

    if (tableWrapper) {
      tableWrapper.innerHTML = `
        <div style="padding: 30px; text-align: center; color: #64748b; font-family: 'Rajdhani', sans-serif; font-size: 13px;">
          Connecting to Central Command Telemetry (${mode === '2v2' ? '2v2 DUO' : '1v1 DUEL'})...
        </div>
      `;
    }

    const is2v2 = (mode === '2v2');
    const jsonPath = is2v2 ? `api/leaderboard_2v2.json?t=${Date.now()}` : `api/leaderboard.json?t=${Date.now()}`;
    const phpPath = is2v2
      ? `api/leaderboard.php?mode=2v2${auth?.accountId ? `&accountId=${encodeURIComponent(auth.accountId)}` : ''}`
      : `api/leaderboard.php${auth?.accountId ? `?accountId=${encodeURIComponent(auth.accountId)}` : ''}`;

    try {
      const [boardRes, myRes] = await Promise.all([
        fetch(jsonPath),
        fetch(phpPath)
      ]);

      const board = (boardRes.ok ? await boardRes.json().catch(() => null) : null) || {};
      const myData = (myRes.ok ? await myRes.json().catch(() => null) : null) || {};
      const boardList = Array.isArray(board.leaderboard) ? board.leaderboard : [];
      const myList = Array.isArray(myData.leaderboard) ? myData.leaderboard : [];
      const leaderboard = myList.length >= boardList.length ? myList : boardList;
      const data = { leaderboard, myAccount: myData.myAccount };

      if (!Array.isArray(data.leaderboard)) {
        if (tableWrapper) tableWrapper.innerHTML = '<div style="padding: 24px; text-align: center; color: #ff4455;">Failed to load leaderboard.</div>';
        return;
      }

      // Populate Left Hero Card with live account stats
      const rankBadge = layer.querySelector('#leaderboard-rank-badge');
      const myMmrEl = layer.querySelector('#leaderboard-my-mmr');
      const tierProgress = layer.querySelector('#leaderboard-tier-progress');
      const tierNextHint = layer.querySelector('#leaderboard-tier-next-hint');
      const winrateLabel = layer.querySelector('#leaderboard-winrate-label');
      const recordStat = layer.querySelector('#leaderboard-record-stat');
      const winrateFill = layer.querySelector('#leaderboard-winrate-fill');
      const recordSub = layer.querySelector('#leaderboard-record-sub');

      if (data.myAccount) {
        const my = data.myAccount;
        const tier = getRankTier(my.mmr);
        if (rankBadge) {
          rankBadge.innerHTML = `
            <span style="font-family: 'Orbitron', sans-serif; font-size: 26px; font-weight: 800; color: ${my.rank <= 3 ? '#fbbf24' : '#00f0ff'};">#${my.rank}</span>
            <div style="font-family: 'Rajdhani', sans-serif; font-size: 10px; font-weight: 700; color: #64748b; letter-spacing: 1px;">GLOBAL RANK</div>
          `;
        }
        if (myMmrEl) myMmrEl.textContent = my.mmr;
        if (tierProgress) {
          tierProgress.style.width = `${Math.min(100, Math.max(10, ((my.mmr - 1000) / 1000) * 100))}%`;
          tierProgress.style.background = tier.color;
        }
        if (tierNextHint) tierNextHint.textContent = `Combat Tier: ${tier.name}`;
        if (winrateLabel) winrateLabel.textContent = `${my.winRate}% WIN`;
        if (recordStat) recordStat.textContent = `${my.wins}W - ${my.losses}L`;
        if (winrateFill) winrateFill.style.width = `${my.winRate}%`;
        if (recordSub) recordSub.textContent = `Total Matches: ${Number(my.wins) + Number(my.losses)}`;
      } else {
        const fallbackMmr = getMMR(mode);
        const fallbackTier = getRankTier(fallbackMmr);
        if (myMmrEl) myMmrEl.textContent = fallbackMmr;
        if (tierProgress) {
          tierProgress.style.width = `${Math.min(100, Math.max(10, ((fallbackMmr - 1000) / 1000) * 100))}%`;
          tierProgress.style.background = fallbackTier.color;
        }
        if (tierNextHint) tierNextHint.textContent = `Combat Tier: ${fallbackTier.name}`;
        if (recordStat) recordStat.textContent = '0W - 0L';
        if (winrateLabel) winrateLabel.textContent = '--% WIN';
        if (rankBadge) {
          rankBadge.innerHTML = `
            <span style="font-family: 'Orbitron', sans-serif; font-size: 26px; font-weight: 800; color: #00f0ff;">--</span>
            <div style="font-family: 'Rajdhani', sans-serif; font-size: 10px; font-weight: 700; color: #64748b; letter-spacing: 1px;">GLOBAL RANK</div>
          `;
        }
      }

      // Render Table
      if (data.leaderboard.length === 0) {
        if (tableWrapper) {
          tableWrapper.innerHTML = `
            <div style="padding: 36px 20px; text-align: center;">
              <div style="font-family: 'Rajdhani', sans-serif; font-size: 15px; font-weight: 700; letter-spacing: 1.5px; color: #00f0ff; margin-bottom: 6px;">
                ${is2v2 ? '2v2 DUO SATELLITE ONLINE // LADDER STANDBY' : 'ARENA SATELLITE ONLINE // LADDER STANDBY'}
              </div>
              <div style="font-size: 12.5px; color: #94a3b8; line-height: 1.6;">
                Awaiting combat reports. Deploy into ${is2v2 ? '2v2 Duo Battles' : 'the Arena'} to claim Rank #1!
              </div>
            </div>
          `;
        }
        return;
      }

      let rowsHtml = `
        <table class="showroom-ladder-table">
          <thead>
            <tr>
              <th class="rank-col">Rank</th>
              <th class="name-col">Commander</th>
              <th class="tier-col">Tier</th>
              <th class="mmr-col">MMR</th>
              <th class="record-col">Record</th>
            </tr>
          </thead>
          <tbody>
      `;

      data.leaderboard.forEach((p, idx) => {
        const tier = getRankTier(p.mmr);
        const isMe = auth?.accountId && p.id === auth.accountId;
        const rankColor = p.rank === 1 ? '#fbbf24' : (p.rank === 2 ? '#e2e8f0' : (p.rank === 3 ? '#f59e0b' : '#94a3b8'));
        const rowClass = isMe ? 'is-me' : (idx % 2 === 0 ? 'even' : 'odd');

        rowsHtml += `
          <tr class="${rowClass}">
            <td class="rank-col" style="color: ${rankColor}; font-weight: 700; font-family: 'IBM Plex Mono', monospace;">
              #${p.rank}
            </td>
            <td class="name-col ${isMe ? 'my-name' : ''}">
              ${escapeHtml(p.displayName)}
            </td>
            <td class="tier-col" style="color: ${tier.color}; font-weight: 600;">
              ${tier.icon} ${tier.name}
            </td>
            <td class="mmr-col" style="font-family: 'IBM Plex Mono', monospace;">
              ${p.mmr}
            </td>
            <td class="record-col" style="font-family: 'IBM Plex Mono', monospace;">
              ${p.wins}W · ${p.losses}L
            </td>
          </tr>
        `;
      });

      rowsHtml += '</tbody></table>';
      if (tableWrapper) tableWrapper.innerHTML = rowsHtml;

    } catch (err) {
      if (tableWrapper) {
        tableWrapper.innerHTML = '<div style="padding: 24px; text-align: center; color: #ff4455;">Connection to Central Command telemetry failed.</div>';
      }
    }
  };

  btn1v1?.addEventListener('click', () => {
    audio?.playUiClick?.();
    loadModeData('1v1');
  });
  btn2v2?.addEventListener('click', () => {
    audio?.playUiClick?.();
    loadModeData('2v2');
  });

  loadModeData('1v1');
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
